import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import RenewalGate from "./components/RenewalGate";
import { AudioProvider } from "./context/AudioContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { CartProvider } from "./context/CartContext";
import { VideoPlayerProvider } from "./context/VideoPlayerContext";
import { ThemeProvider } from "./context/ThemeContext";
import { useEffect, lazy, Suspense } from "react";
import { registerServiceWorker, requestNotificationPermission, subscribeToPush } from "./utils/pushNotifications";
import BuyToUnlockPrompt from "./components/BuyToUnlockPrompt";
import OnboardingFlow from "./components/OnboardingFlow";
import useAnalyticsTracking from "./hooks/useAnalyticsTracking";
import TermsAcceptanceModal from "./components/TermsAcceptanceModal";
import ShoppingCartModal from "./components/ShoppingCartModal";
import MiniPlayer from "./components/MiniPlayer";
import AIStudioLauncher from "./components/AIStudioLauncher";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#00CCFF] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Critical pages (loaded immediately)
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Home from "./pages/Home";
import { Layout } from "./components/layout/Layout";

// Lazy loaded pages (loaded on demand)
const Browse = lazy(() => import("./pages/Browse"));
const ArtistDashboard = lazy(() => import("./pages/ArtistDashboard"));
const TrackDetail = lazy(() => import("./pages/TrackDetail"));
const VideoDetail = lazy(() => import("./pages/VideoDetail"));
const Library = lazy(() => import("./pages/Library"));
const Playlists = lazy(() => import("./pages/Playlists"));
const Upload = lazy(() => import("./pages/Upload"));
const Profile = lazy(() => import("./pages/Profile"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const CardCheckoutSuccess = lazy(() => import("./pages/CardCheckoutSuccess"));
const AlbumDetail = lazy(() => import("./pages/AlbumDetail"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminISRCRegistry = lazy(() => import("./pages/AdminISRCRegistry"));
const AdminPaidToListen = lazy(() => import("./pages/AdminPaidToListen"));
const AdminMerchDisputes = lazy(() => import("./pages/AdminMerchDisputes"));
const FeesPage = lazy(() => import("./pages/FeesPage"));
const CreatorProPage = lazy(() => import("./pages/CreatorProPage"));
const ProPlansPage = lazy(() => import("./pages/ProPlansPage"));
const ObsQueueOverlay = lazy(() => import("./pages/ObsQueueOverlay"));
const MyShopPage = lazy(() => import("./pages/MyShopPage"));
const PaidToListen = lazy(() => import("./pages/PaidToListen"));
const PaidToListenInfo = lazy(() => import("./pages/PaidToListenInfo"));
const AdvertiserDashboard = lazy(() => import("./pages/AdvertiserDashboard"));
const DJHub = lazy(() => import("./pages/DJHub"));
const SharedPlaylist = lazy(() => import("./pages/SharedPlaylist"));
const FreePlayer = lazy(() => import("./pages/FreePlayer"));
const OneVoiceRadio = lazy(() => import("./pages/OneVoiceRadio"));
const Messages = lazy(() => import("./pages/Messages"));
const Groups = lazy(() => import("./pages/Groups"));
const CollaborationBoard = lazy(() => import("./pages/CollaborationBoard"));
const VerificationSuccess = lazy(() => import("./pages/VerificationSuccess"));
const Settings = lazy(() => import("./pages/Settings"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const DonateSuccess = lazy(() => import("./pages/DonateSuccess"));
const MerchSuccess = lazy(() => import("./pages/MerchSuccess"));
const BeatDetail = lazy(() => import("./pages/BeatDetail"));
const Earnings = lazy(() => import("./pages/Earnings"));
const PublicReviewerPage = lazy(() => import("./pages/PublicReviewerPage"));
const MerchMarketplace = lazy(() => import("./pages/MerchMarketplace"));
const OneVoiceStudio = lazy(() => import("./pages/OneVoiceStudio"));
const StudioSessionsList = lazy(() => import("./pages/StudioSessionsList"));
const StudioSessionEditor = lazy(() => import("./pages/StudioSessionEditor"));
const StudioPublicView = lazy(() => import("./pages/StudioPublicView"));
const AIStudio = lazy(() => import("./pages/AIStudio"));
const AIStudioARScout = lazy(() => import("./pages/AIStudioARScout"));
const AIStudioCoverArt = lazy(() => import("./pages/AIStudioCoverArt"));
const AIStudioSongwriter = lazy(() => import("./pages/AIStudioSongwriter"));
const AIStudioSharedReport = lazy(() => import("./pages/AIStudioSharedReport"));
const AIStudioSharedDraft = lazy(() => import("./pages/AIStudioSharedDraft"));
const ProducerPacks = lazy(() => import("./pages/ProducerPacks"));
const ProducerPackUpload = lazy(() => import("./pages/ProducerPackUpload"));
const ProducerPackDetail = lazy(() => import("./pages/ProducerPackDetail"));
const ProducerPackLibrary = lazy(() => import("./pages/ProducerPackLibrary"));
const ShortLinkRedirect = lazy(() => import("./pages/ShortLinkRedirect"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const MerchItemDetail = lazy(() => import("./pages/MerchItemDetail"));
const CartPage = lazy(() => import("./pages/CartPage"));
// RevenueWithdrawals page removed — earnings now consolidated on /earnings
const PayoutReceipt = lazy(() => import("./pages/PayoutReceipt"));
const BeatLicenseSign = lazy(() => import("./pages/BeatLicenseSign"));
const PlaylistDetail = lazy(() => import("./pages/PlaylistDetail"));
const AIRadio = lazy(() => import("./pages/AIRadio"));
const AIDiscover = lazy(() => import("./pages/AIDiscover"));
const AIDetector = lazy(() => import("./pages/AIDetector"));
const PerformanceMonitor = lazy(() => import("./pages/PerformanceMonitor"));
const ArtistDiscover = lazy(() => import("./pages/ArtistDiscover"));
const Royalties = lazy(() => import("./pages/Royalties"));
const SyncCatalog = lazy(() => import("./pages/SyncCatalog"));
const FanFeed = lazy(() => import("./pages/FanFeed"));
const ListenerWall = lazy(() => import("./pages/ListenerWall"));
const TourPublicPage = lazy(() => import("./pages/TourPublicPage"));
const MySubscriptions = lazy(() => import("./pages/MySubscriptions"));
const CopyrightDetailPage = lazy(() => import("./pages/CopyrightDetailPage"));
const ProducerDiscover = lazy(() => import("./pages/ProducerDiscover"));
const VIPPage = lazy(() => import("./pages/VIPPage"));
const ListeningRoomPage = lazy(() => import("./pages/ListeningRoomPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const MyTicketsPage = lazy(() => import("./pages/MyTicketsPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
// New comprehensive Events system
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const MyTickets = lazy(() => import("./pages/MyTickets"));
const MyEvents = lazy(() => import("./pages/MyEvents"));
const MyListingsPage = lazy(() => import("./pages/MyListingsPage"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const FreeVideos = lazy(() => import("./pages/FreeVideos"));
const Analytics = lazy(() => import("./pages/Analytics"));
const CreatorAnalytics = lazy(() => import("./pages/CreatorAnalytics"));
const MusicReviewPage = lazy(() => import("./pages/MusicReviewPage"));
const ReviewSessionPage = lazy(() => import("./pages/ReviewSessionPage"));
const ReviewerDashboard = lazy(() => import("./pages/ReviewerDashboard"));
const SessionCodeRouter = lazy(() => import("./pages/SessionCodeRouter"));
const SongWarsPage = lazy(() => import("./pages/SongWarsPage"));
const SongWarsLeaderboardPage = lazy(() => import("./pages/SongWarsLeaderboardPage"));
const SocialMediaKit = lazy(() => import("./pages/SocialMediaKit"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const UsernameProfile = lazy(() => import("./pages/UsernameProfile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CustomLandingPage = lazy(() => import("./pages/CustomLandingPage"));
const CreatorProPlusDashboard = lazy(() => import("./pages/CreatorProPlusDashboard"));
const DJDashboard = lazy(() => import("./pages/DJDashboard"));
const GigMarketplace = lazy(() => import("./pages/GigMarketplace"));
const PromoterDashboard = lazy(() => import("./pages/PromoterDashboard"));
const Communities = lazy(() => import("./pages/Communities"));
const CommunityDetail = lazy(() => import("./pages/CommunityDetail"));
const MoodPlaylists = lazy(() => import("./pages/MoodPlaylists"));
const EventMap = lazy(() => import("./pages/EventMap"));
const LiveSessions = lazy(() => import("./pages/LiveSessions"));
const LiveSessionRoom = lazy(() => import("./pages/LiveSessionRoom"));
const ListeningPartiesList = lazy(() => import("./pages/ListeningPartiesList"));
const ListeningPartyPage = lazy(() => import("./pages/ListeningPartyPage"));
const NotificationPreferencesPage = lazy(() => import("./pages/NotificationPreferencesPage"));
const AdminNotificationsDashboard = lazy(() => import("./pages/AdminNotificationsDashboard"));
const InvoiceGenerator = lazy(() => import("./pages/InvoiceGenerator"));
const FlyerGenerator = lazy(() => import("./pages/FlyerGenerator"));
const SharedAdAnalytics = lazy(() => import("./pages/SharedAdAnalytics"));
const WeeklyTopTracks = lazy(() => import("./pages/WeeklyTopTracks"));
const MixMasterOrderPage = lazy(() => import("./pages/MixMasterOrderPage"));
const MixMasterOrdersPage = lazy(() => import("./pages/MixMasterOrdersPage"));
const MixMasterSearchPage = lazy(() => import("./pages/MixMasterSearchPage"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const EmbedPlayer = lazy(() => import("./pages/EmbedPlayer"));
const TicketConfirmation = lazy(() => import("./pages/TicketConfirmation"));
const EventScanPage = lazy(() => import("./pages/EventScanPage"));
const EventCohostPage = lazy(() => import("./pages/EventCohostPage"));
const TicketValidatePage = lazy(() => import("./pages/TicketValidatePage"));
const EmailBlastPage = lazy(() => import("./pages/EmailBlastPage"));

// Initialize push notifications
const initPushNotifications = async (token) => {
  if (!token) return;
  
  try {
    await registerServiceWorker();
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      await subscribeToPush(token);
    }
  } catch (error) {
    console.error('Push notification setup failed:', error);
  }
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <p className="text-[#888888] font-mono">Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Artist Route - Allows Artists, Producers, AND Admins
const ArtistRoute = ({ children }) => {
  const { isAuthenticated, user, loading, isArtist, isProducer } = useAuth();
  const isAdmin = user?.is_admin === true;
  const isAiArtist = user?.is_ai_artist === true;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <p className="text-[#888888] font-mono">Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow artists, producers, AI artists, DJs, promoters, and admins
  if (!isArtist && !isProducer && !isAiArtist && !isAdmin && !user?.is_dj && !user?.is_promoter) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Public Route (redirect to home if authenticated, or to change-password if force required)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, forcePasswordChange } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <p className="text-[#888888] font-mono">Loading...</p>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // If user needs to change password, redirect there instead of home
    if (forcePasswordChange) {
      return <Navigate to="/change-password?required=true" replace />;
    }
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><Layout><Landing /></Layout></PublicRoute>} />

        {/* OBS browser-source overlay — transparent, no chrome, public-read */}
        <Route path="/obs/queue/:code" element={<ObsQueueOverlay />} />
        <Route path="/login" element={<PublicRoute><Layout><Login /></Layout></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Layout><Register /></Layout></PublicRoute>} />
        
        {/* Protected Routes */}
        <Route path="/home" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
        <Route path="/playlists" element={<ProtectedRoute><Playlists /></ProtectedRoute>} />
        <Route path="/playlists/:id" element={<ProtectedRoute><PlaylistDetail /></ProtectedRoute>} />
        {/* Public share-target routes — anyone with a link should be
         * able to view tracks/albums without logging in. Sales/purchase
         * actions still require auth (handled inside the page). */}
        <Route path="/track/:id" element={<TrackDetail />} />
        <Route path="/beat/:id" element={<ProtectedRoute><BeatDetail /></ProtectedRoute>} />
        <Route path="/beat-license/:id" element={<ProtectedRoute><BeatLicenseSign /></ProtectedRoute>} />
        <Route path="/video/:id" element={<ProtectedRoute><VideoDetail /></ProtectedRoute>} />
        <Route path="/album/:id" element={<AlbumDetail />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<Profile />} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
        <Route path="/checkout/cancel" element={<ProtectedRoute><CheckoutCancel /></ProtectedRoute>} />
        <Route path="/checkout/card-success" element={<ProtectedRoute><CardCheckoutSuccess /></ProtectedRoute>} />
        <Route path="/donate/success" element={<ProtectedRoute><DonateSuccess /></ProtectedRoute>} />
        <Route path="/merch/success" element={<ProtectedRoute><MerchSuccess /></ProtectedRoute>} />
      
      {/* Password Reset Routes (Public) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Email Verification */}
      <Route path="/verify-email" element={<ProtectedRoute><VerifyEmail /></ProtectedRoute>} />
      
      {/* Artist Routes */}
      <Route path="/dashboard" element={<ArtistRoute><ArtistDashboard /></ArtistRoute>} />
      <Route path="/upload" element={<ArtistRoute><Upload /></ArtistRoute>} />
      <Route path="/earnings" element={<ArtistRoute><Earnings /></ArtistRoute>} />
      {/* iter_483 — Royalties & Publishing + Sync Catalog */}
      <Route path="/royalties" element={<ArtistRoute><Royalties /></ArtistRoute>} />
      <Route path="/sync-catalog" element={<SyncCatalog />} />
      {/* iter_485 — Fan-subscription drops feed (works for guests too — shows public teasers) */}
      <Route path="/fan-feed" element={<FanFeed />} />
      {/* iter_486 — Public Listener Wall + Public Tour Pages */}
      <Route path="/listener-wall" element={<ListenerWall />} />
      <Route path="/tour/:username/:slug" element={<TourPublicPage />} />
      {/* iter_487 — Unified My Subscriptions */}
      <Route path="/my-subscriptions" element={<MySubscriptions />} />
      <Route path="/u/:handle" element={<PublicReviewerPage />} />
      <Route path="/merch" element={<MerchMarketplace />} />
      <Route path="/studio" element={<OneVoiceStudio />} />
      <Route path="/studio/sessions" element={<ProtectedRoute><StudioSessionsList /></ProtectedRoute>} />
      <Route path="/studio/sessions/:id" element={<ProtectedRoute><StudioSessionEditor /></ProtectedRoute>} />
      <Route path="/s/:token" element={<StudioPublicView />} />
      <Route path="/ai-studio" element={<AIStudio />} />
      <Route path="/ai-studio/ar-scout" element={<AIStudioARScout />} />
      <Route path="/ai-studio/cover-art" element={<AIStudioCoverArt />} />
      <Route path="/ai-studio/songwriter" element={<AIStudioSongwriter />} />
      <Route path="/ai-studio/shared/ar-scout/:shareId" element={<AIStudioSharedReport />} />
      <Route path="/ai-studio/shared/songwriter/:shareId" element={<AIStudioSharedDraft />} />
      <Route path="/producer-packs" element={<ProducerPacks />} />
      <Route path="/producer-packs/new" element={<ProtectedRoute><ProducerPackUpload /></ProtectedRoute>} />
      <Route path="/producer-packs/library" element={<ProtectedRoute><ProducerPackLibrary /></ProtectedRoute>} />
      <Route path="/producer-packs/:id" element={<ProducerPackDetail />} />
      <Route path="/r/:slug" element={<ShortLinkRedirect />} />
      <Route path="/merch/wishlist" element={<WishlistPage />} />
      <Route path="/merch/item/:id" element={<MerchItemDetail />} />
      <Route path="/cart" element={<CartPage />} />
      {/* /merch/cart — same component, lives under the merch namespace so
          the shop+cart sub-tab stays visually grouped */}
      <Route path="/merch/cart" element={<CartPage />} />
      <Route path="/payout/:payoutId" element={<ProtectedRoute><PayoutReceipt /></ProtectedRoute>} />
      <Route path="/analytics" element={<ArtistRoute><Analytics /></ArtistRoute>} />
      <Route path="/messages" element={<ArtistRoute><Messages /></ArtistRoute>} />
      <Route path="/messages/:partnerId" element={<ArtistRoute><Messages /></ArtistRoute>} />
      <Route path="/groups" element={<Navigate to="/communities" replace />} />
      <Route path="/groups/:groupId" element={<Navigate to="/communities" replace />} />
      <Route path="/verification/success" element={<ArtistRoute><VerificationSuccess /></ArtistRoute>} />
      
      {/* Password Change (for admin reset) */}
      <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
      
      {/* Collaboration Board */}
      <Route path="/collaborations" element={<CollaborationBoard />} />
      
      {/* Communities */}
      <Route path="/communities" element={<Communities />} />
      <Route path="/community/:communityId" element={<ProtectedRoute><CommunityDetail /></ProtectedRoute>} />
      
      {/* Mood Playlists */}
      <Route path="/mood-playlists" element={<MoodPlaylists />} />
      
      {/* Event Map */}
      <Route path="/event-map" element={<EventMap />} />
      
      {/* Live Sessions */}
      <Route path="/live-sessions" element={<LiveSessions />} />
      <Route path="/live-session/:sessionId" element={<ProtectedRoute><LiveSessionRoom /></ProtectedRoute>} />

      {/* Listening Parties */}
      <Route path="/listening-parties" element={<ListeningPartiesList />} />
      <Route path="/listening-parties/:id" element={<ListeningPartyPage />} />

      {/* Notification preferences + admin dashboard */}
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotificationsDashboard /></ProtectedRoute>} />
      
      {/* Invoice Generator */}
      <Route path="/invoices" element={<ProtectedRoute><InvoiceGenerator /></ProtectedRoute>} />
      
      {/* Flyer Generator */}
      <Route path="/flyer-generator" element={<ProtectedRoute><FlyerGenerator /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/barcode-registry" element={<ProtectedRoute><AdminISRCRegistry /></ProtectedRoute>} />
      <Route path="/admin/paid-to-listen" element={<ProtectedRoute><AdminPaidToListen /></ProtectedRoute>} />
      <Route path="/admin/merch-disputes" element={<ProtectedRoute><AdminMerchDisputes /></ProtectedRoute>} />
      <Route path="/fees" element={<FeesPage />} />
      <Route path="/advertiser" element={<ProtectedRoute><AdvertiserDashboard /></ProtectedRoute>} />
      <Route path="/radio-stations" element={<ProtectedRoute><DJHub /></ProtectedRoute>} />

      {/* Paid To Listen — listener dashboard / subscription / payout handles */}
      <Route path="/paid-to-listen" element={<ProtectedRoute><PaidToListen /></ProtectedRoute>} />
      {/* Paid To Listen — public marketing / how-it-works info page */}
      <Route path="/paid-to-listen/info" element={<PaidToListenInfo />} />
      <Route path="/paid-to-listen-info" element={<PaidToListenInfo />} />
      
      {/* Public Playlist Share Route */}
      <Route path="/playlist/share/:shareCode" element={<SharedPlaylist />} />
      
      {/* Public Ad Analytics Share Route */}
      <Route path="/ad-report/:token" element={<SharedAdAnalytics />} />
      
      {/* One Voice Radio — unified server-synced radio with 4 tabs:
          All Genre / Music / AI / Beats. Replaces the old separate
          Free Radio + AI Radio pages. */}
      <Route path="/radio" element={<OneVoiceRadio />} />

      {/* Free Player Routes — kept for backwards-compat deep-links into
          the legacy custom-playlist player. New listeners go to /radio. */}
      <Route path="/free-player" element={<FreePlayer />} />
      <Route path="/musicradio" element={<Navigate to="/radio?station=music" replace />} />
      <Route path="/beatsradio" element={<Navigate to="/radio?station=beats" replace />} />
      <Route path="/freeradio" element={<Navigate to="/radio?station=all" replace />} />

      {/* AI Radio — legacy URL redirects into the AI tab of the unified page. */}
      <Route path="/ai-radio" element={<Navigate to="/radio?station=ai" replace />} />
      <Route path="/airadio" element={<Navigate to="/radio?station=ai" replace />} />
      
      {/* AI Discovery Route */}
      <Route path="/ai-discover" element={<AIDiscover />} />
      <Route path="/ai-detector" element={<Layout><AIDetector /></Layout>} />
      
      {/* Performance Monitor (Admin Only) */}
      <Route path="/admin/performance" element={<ProtectedRoute><PerformanceMonitor /></ProtectedRoute>} />
      
      {/* Copyright Review (Admin Only) */}
      <Route path="/admin/copyright/:contentType/:contentId" element={<ProtectedRoute><CopyrightDetailPage /></ProtectedRoute>} />
      
      {/* Leaderboard Route */}
      <Route path="/leaderboard" element={<Leaderboard />} />
      
      {/* Videos Streaming */}
      <Route path="/videos" element={<FreeVideos />} />
      <Route path="/videos/:videoId" element={<FreeVideos />} />
      {/* Legacy routes for backwards compatibility */}
      <Route path="/free-videos" element={<FreeVideos />} />
      <Route path="/free-videos/:videoId" element={<FreeVideos />} />
      
      {/* Creator Analytics Dashboard */}
      <Route path="/creator-analytics" element={<ArtistRoute><CreatorAnalytics /></ArtistRoute>} />
      
      {/* Discover Routes */}
      <Route path="/discover/artists" element={<ArtistDiscover />} />
      <Route path="/discover/producers" element={<ProducerDiscover />} />
      
      {/* VIP Page */}
      <Route path="/vip" element={<VIPPage />} />
      <Route path="/vip/listening-room/:id" element={<ProtectedRoute><ListeningRoomPage /></ProtectedRoute>} />

      {/* Creator Pro — $9.99 / $14.99 monthly subscription for verified creators */}
      <Route path="/creator-pro" element={<ProtectedRoute><CreatorProPage /></ProtectedRoute>} />
      <Route path="/pro-plans" element={<ProtectedRoute><ProPlansPage /></ProtectedRoute>} />

      {/* My Shop — standalone merch-seller dashboard */}
      <Route path="/my-shop" element={<ProtectedRoute><MyShopPage /></ProtectedRoute>} />
      
      {/* Events & Tickets - New System */}
      <Route path="/events" element={<Events />} />
      <Route path="/events/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
      <Route path="/events/my-events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
      <Route path="/events/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
      <Route path="/events/:eventId" element={<EventDetailPage />} />
      <Route path="/events/:eventId/scan" element={<ProtectedRoute><EventScanPage /></ProtectedRoute>} />
      <Route path="/events/:eventId/cohost/:token" element={<EventCohostPage />} />
      <Route path="/ticket-confirmation/:ticketId" element={<TicketConfirmation />} />
      <Route path="/ticket/validate/:ticketId" element={<TicketValidatePage />} />
      <Route path="/email-blast" element={<ProtectedRoute><EmailBlastPage /></ProtectedRoute>} />
      
      {/* Legacy Ticket System */}
      <Route path="/tickets/my-tickets" element={<MyTicketsPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/my-listings" element={<MyListingsPage />} />
      
      {/* Help & FAQ */}
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/faq" element={<FAQPage />} />
      
      {/* Weekly Top Tracks (public, SEO-friendly) */}
      <Route path="/top-tracks" element={<WeeklyTopTracks />} />
      <Route path="/top-tracks/weekly" element={<WeeklyTopTracks />} />
      <Route path="/mix-master/search" element={<MixMasterSearchPage />} />
      <Route path="/mix-master/orders" element={<ProtectedRoute><MixMasterOrdersPage /></ProtectedRoute>} />
      <Route path="/mix-master/order/:orderId" element={<ProtectedRoute><MixMasterOrderPage /></ProtectedRoute>} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/p/:username" element={<CustomLandingPage />} />
      <Route path="/creator-pro/plus" element={<CreatorProPlusDashboard />} />
      
      {/* DJ & Promoter Features */}
      <Route path="/dj" element={<ProtectedRoute><DJDashboard /></ProtectedRoute>} />
      <Route path="/gigs" element={<GigMarketplace />} />
      <Route path="/promoter" element={<ProtectedRoute><PromoterDashboard /></ProtectedRoute>} />
      
      {/* Music Review */}
      <Route path="/music-review" element={<MusicReviewPage />} />
      <Route path="/review/:sessionCode" element={<ReviewSessionPage />} />
      <Route path="/reviewer" element={<ReviewerDashboard />} />
      
      {/* Song Wars */}
      <Route path="/sw/:username" element={<SongWarsPage />} />
      <Route path="/song-wars/leaderboard" element={<SongWarsLeaderboardPage />} />

      {/* Social Media Kit */}
      <Route path="/social-kit" element={<ProtectedRoute><SocialMediaKit /></ProtectedRoute>} />
      
      {/* Public Profile - Shareable profile link by username (e.g., /u/ArtistName) */}
      <Route path="/u/:username" element={<PublicProfile />} />
      
      {/* Root-level session code (e.g., /WJ9PQMIF) - must be near the end */}
      <Route path="/embed/player/:trackId" element={<EmbedPlayer />} />
      <Route path="/embed/playlist/:playlistId" element={<EmbedPlayer />} />

      <Route path="/:code" element={<SessionCodeRouter />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// Component to initialize push notifications after auth
function PushNotificationInit() {
  const { token, isAuthenticated } = useAuth();
  
  useEffect(() => {
    if (isAuthenticated && token) {
      initPushNotifications(token);
    }
  }, [isAuthenticated, token]);
  
  return null;
}

// Unified onboarding flow wrapper (replaces separate ProfileReminderPopup + StripeSetupReminder)

// Analytics tracking component - tracks all visitors
function AnalyticsTracker() {
  useAnalyticsTracking();
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <WebSocketProvider>
          <AudioProvider>
            <CartProvider>
              <VideoPlayerProvider>
              <AnalyticsTracker />
              <PushNotificationInit />
              <RenewalGate />
              <OnboardingFlow />
              <TermsAcceptanceModal />
              <AppRoutes />
              <BuyToUnlockPrompt />
              <ShoppingCartModal />
              <MiniPlayer />
              <AIStudioLauncher />
              <Toaster 
                position="bottom-right" 
                toastOptions={{
                  style: {
                    background: '#0a0a0a',
                    border: '1px solid #1a1a1a',
                    color: '#FFFFFF',
                    fontFamily: 'JetBrains Mono, monospace',
                  },
                }}
              />
              </VideoPlayerProvider>
            </CartProvider>
          </AudioProvider>
        </WebSocketProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
